/**
 * Normalised auth claims shared by all auth backends.
 * Cognito-specific decode logic lives in AuthProvider.
 */
export interface Claims {
  sub: string;
  email: string;
  role: string;
  regions: string[];
  systems: string[];
}

export const FAKE_CLAIMS: Claims = {
  sub: 'dev-user',
  email: 'dev@local',
  role: 'platform-admin',
  regions: ['all'],
  systems: ['events', 'audience', 'polls', 'macro', 'media', 'competition'],
};

/**
 * Shape of the OIDC user object surfaced by react-oidc-context.
 * Only the fields we care about — see `oidc-client-ts` `User` for the full shape.
 */
export interface OidcProfileLike {
  sub?: string;
  email?: string;
  ['custom:role']?: string;
  ['custom:regions']?: string;
  [key: string]: unknown;
}

/**
 * Map an OIDC `user.profile` object to our normalised `Claims` shape.
 *
 * Cognito custom attributes arrive as `custom:role` / `custom:regions` strings.
 * `custom:regions` is a CSV ("tel-aviv,jerusalem" or "all"). When the
 * pre-token-generation Lambda hasn't enriched the token yet (e.g. the user
 * just signed up), we default to the most restrictive: `viewer` / no regions.
 */
export function profileToClaims(profile: OidcProfileLike | undefined | null): Claims {
  const sub = typeof profile?.sub === 'string' ? profile.sub : '';
  const email = typeof profile?.email === 'string' ? profile.email : '';
  const role =
    typeof profile?.['custom:role'] === 'string' && profile['custom:role']!.length > 0
      ? profile['custom:role']!
      : 'viewer';
  const regionsRaw = profile?.['custom:regions'];
  const regions =
    typeof regionsRaw === 'string' && regionsRaw.length > 0
      ? regionsRaw
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean)
      : [];
  return { sub, email, role, regions, systems: [] };
}
